import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { Plus, Pin } from "lucide-react";
import QuickActionSheet from "./QuickActionSheet";
import { trackEvent } from "../lib/analytics";

/*
  Mobile bottom dock — full-width frosted bar pinned to the viewport bottom.
  - Left slot: "+" opens quick-action sheet (scan · todo · measure · calendar · share)
  - Remaining 3 slots: user's top favourites (auto-recorded by usage)
  - Show/hide behaviour: visible on scroll up, at top-of-page, at bottom-of-page
    and after 1s inactivity. Hidden while the user is actively scrolling down.
*/
export default function MobileDock({ favTools, pins }) {
  const [qaOpen, setQaOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const idleTimer = useRef(null);

  useEffect(() => {
    lastY.current = window.scrollY;
    function onScroll() {
      const y = window.scrollY;
      const dy = y - lastY.current;
      const atTop = y < 16;
      const atBottom = window.innerHeight + y >= document.body.offsetHeight - 32;
      if (atTop || atBottom) {
        setHidden(false);
      } else if (dy > 4) {
        setHidden(true);
      } else if (dy < -4) {
        setHidden(false);
      }
      lastY.current = y;
      // reappear after 1s of inactivity
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => setHidden(false), 1000);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, []);

  const items = favTools.slice(0, 3);

  return (
    <>
      <div
        data-testid="mobile-dock"
        aria-hidden={hidden}
        className="md:hidden fixed left-0 right-0 bottom-0 z-40 no-print transition-transform duration-300"
        style={{ transform: hidden ? "translateY(120%)" : "translateY(0)" }}
      >
        <div
          className="mx-3 mb-[max(12px,env(safe-area-inset-bottom))] rounded-2xl border flex items-center gap-1 p-1.5"
          style={{
            background: "color-mix(in srgb, var(--surface) 82%, transparent)",
            borderColor: "var(--border-subtle)",
            backdropFilter: "saturate(1.4) blur(16px)",
            WebkitBackdropFilter: "saturate(1.4) blur(16px)",
            boxShadow: "0 12px 30px -10px rgba(0,0,0,0.25), 0 1px 0 rgba(255,255,255,0.04) inset",
          }}
        >
          <button
            onClick={() => { setQaOpen(true); trackEvent("quick_action_opened", { source: "dock" }); }}
            data-testid="dock-quick-action"
            className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0 transition-transform active:scale-95"
            style={{ background: "var(--primary)", color: "white" }}
            aria-label="Quick actions"
          >
            <Plus className="h-6 w-6" />
          </button>

          <div className="flex-1 flex items-center justify-around gap-1">
            {items.length > 0 ? items.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`${testid}-dock`}
                end={to === "/"}
                className={({ isActive }) =>
                  `h-12 flex-1 max-w-[80px] rounded-xl flex flex-col items-center justify-center gap-0.5 transition-colors relative ${
                    isActive ? "text-[color:var(--primary-ink)]" : "text-[color:var(--text-muted)] hover:text-[color:var(--text-main)]"
                  }`
                }
                style={({ isActive }) => isActive ? { background: "var(--primary-soft)" } : {}}
                title={label}
              >
                <Icon className="h-[18px] w-[18px]" />
                <span className="text-[10px] font-medium truncate max-w-full px-1">{label}</span>
                {pins[to] && <Pin className="h-2.5 w-2.5 absolute top-1 right-1.5" style={{ color: "var(--accent)" }} />}
              </NavLink>
            )) : (
              <div className="text-[11px] px-3" style={{ color: "var(--text-muted)" }}>
                Open a tool to pin it here.
              </div>
            )}
          </div>
        </div>
      </div>
      <QuickActionSheet open={qaOpen} onClose={() => setQaOpen(false)} />
    </>
  );
}
